import { Link } from 'react-router-dom';
import { EditableMarkdownField, EditableText, useSectionEditing } from '@kevlarcon/pages';
import { useAuth } from '../../contexts/AuthContext';

type PartnerEntryProps = {
  heading: string;
  lede?: string;
};

/**
 * The `PartnerEntry` registered section type (see tenant_site/section_types.py,
 * tenant_site/system_pages.py's PartnersPage) — the locked front door on the
 * `/partners` landing page. The buttons are fixed on purpose (an admin edits
 * the copy but can't delete or repoint the way in): "Add your school" goes to
 * the join page and "Partner sign in" to the tenant's magic-link login. A
 * school already signed in sees a link to its own console instead.
 *
 * Built on the CTA strip: same `.cta-strip` band (heading left, buttons right,
 * full-bleed — see PARTNER_ENTRY's `full_bleed`) as `@kevlarcon/pages`'
 * `CtaStrip`, except the buttons are fixed rather than an editable repeat, and
 * an optional lede sits under the heading.
 */
export default function PartnerEntry({ heading, lede }: PartnerEntryProps) {
  const { active } = useSectionEditing();
  const { user, isPartner } = useAuth();

  return (
    <section className="cta-strip partner-entry" data-testid="partner-entry">
      <div className="wrap">
        <div className="partner-entry-copy">
          <EditableText as="h2" kind="richtext" value={heading} path={['heading']} />
          {(lede || active) && <EditableMarkdownField value={lede ?? ''} path={['lede']} />}
        </div>
        <div className="ctas partner-entry-actions">
          {user && isPartner ? (
            <Link to="/partners/dashboard" className="btn btn-primary" data-testid="partner-entry-dashboard-link">
              Go to your dashboard
            </Link>
          ) : (
            <>
              <Link to="/partners/join" className="btn btn-primary" data-testid="partner-entry-join-link">
                Add your school
              </Link>
              <Link to="/login" className="btn btn-ghost" data-testid="partner-entry-login-link">
                Partner sign in
              </Link>
            </>
          )}
        </div>
      </div>
    </section>
  );
}
